// @ts-nocheck -- plain theme JS, not typed
/* Drug-Nutrient Interaction Checker section.
   Defined as a custom element so the section keeps working when the theme
   editor re-renders it (connectedCallback runs again on the new DOM). */
(function () {
  'use strict';

  if (customElements.get('drug-interactions-page')) return;

  const MIN_CHARS = 3;
  const DEBOUNCE_MS = 300;
  // Original ProductsCarousel: productsPerDisplay=4 over an
  // `allProductsForDisplay: products.slice(0, 8)` list.
  const PRODUCTS_PER_PAGE = 4;
  const MAX_PRODUCTS = 8;

  class DrugInteractionsPage extends HTMLElement {
    connectedCallback() {
      if (this.dniInitialized) return;
      this.dniInitialized = true;

      this.sectionId = this.dataset.sectionId || '';
      this.searchApi = this.dataset.searchApi || '/tools/search';
      this.detailApi = this.dataset.detailApi || '';
      this.moneyFormatter = this.buildMoneyFormatter();

      this.input = this.querySelector('.dni-input');
      this.menu = this.querySelector('.dni-menu');
      // The chips/tabs live in the blue box and the panels in the full-bleed
      // white block below it, so both are revealed together.
      this.resultBlocks = [...this.querySelectorAll('[data-results]')];
      this.chipsEl = this.querySelector('.dni-chips');
      this.statusEl = this.querySelector('[data-search-status]');

      this.timer = null;
      this.controller = null;
      this.detailController = null;
      this.onDocumentClick = null;
      this.activeIndex = -1;
      this.selected = {};
      this.pendingDetails = {};
      // Latest interaction payload for the whole selection: the detail API is
      // a batch endpoint, so results are not stored per drug.
      this.interactions = null;
      this.detailCache = {};
      this.productCache = {};
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.initCta();
      this.initAckLink();
      this.initVideoFacades();
      this.initSearch();
      this.initTabs();
    }

    disconnectedCallback() {
      // Theme editor removes this instance and mounts a new one; drop the
      // document-level listener and any pending work so nothing leaks.
      if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
      clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
      if (this.detailController) this.detailController.abort();
    }

    buildMoneyFormatter() {
      try {
        return new Intl.NumberFormat(this.dataset.locale || undefined, {
          style: 'currency',
          currency: this.dataset.currency || 'USD'
        });
      } catch (e) {
        return null;
      }
    }

    formatMoney(cents) {
      if (this.moneyFormatter) return this.moneyFormatter.format(cents / 100);
      return `$${(cents / 100).toFixed(2)}`;
    }

    /* ---- hero CTA -> scroll to search ---- */
    initCta() {
      const cta = this.querySelector('[data-scroll-to-search]');
      if (!cta) return;
      cta.addEventListener('click', () => {
        if (!this.input) return;
        this.input.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'center' });
        setTimeout(() => this.input.focus({ preventScroll: true }), this.reduceMotion ? 0 : 600);
      });
    }

    /* ---- acknowledgement copy -> scroll to the notice at the bottom ---- */
    initAckLink() {
      const ack = this.querySelector('.dni-ack');
      const notice = this.querySelector('.dni-notice');
      if (!ack || !notice) return;
      if (!notice.id) notice.id = `dni-notice-${this.sectionId}`;

      // The copy is merchant-editable rich text, so the phrase may already be
      // a link (with or without a target) or plain text.
      const phrase = /notice and acknowledge?ment/i;
      let link = [...ack.querySelectorAll('a')].find((a) => {
        const href = a.getAttribute('href');
        return !href || href === '#' || phrase.test(a.textContent);
      });
      if (!link) link = this.linkifyPhrase(ack, phrase);
      if (!link) return;

      link.setAttribute('href', `#${notice.id}`);
      link.addEventListener('click', (e) => {
        e.preventDefault();
        notice.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'start' });
      });
    }

    /* Wraps the first text match of `re` in an <a> and returns that link. */
    linkifyPhrase(root, re) {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        const match = node.nodeValue.match(re);
        if (!match) continue;
        const rest = node.splitText(match.index);
        rest.splitText(match[0].length);
        const link = document.createElement('a');
        link.textContent = match[0];
        rest.parentNode.replaceChild(link, rest);
        return link;
      }
      return null;
    }

    /* ---- click-to-play YouTube facades ---- */
    initVideoFacades() {
      this.querySelectorAll('.dni-video-facade').forEach((facade) => {
        facade.addEventListener('click', () => {
          const iframe = document.createElement('iframe');
          iframe.src = facade.dataset.videoSrc;
          iframe.title = facade.dataset.videoTitle || 'Video';
          iframe.setAttribute(
            'allow',
            'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
          );
          iframe.setAttribute('allowfullscreen', '');
          facade.replaceWith(iframe);
        });
      });
    }

    /* ---- medication search combobox ---- */
    initSearch() {
      if (!this.input || !this.menu) return;

      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        if (this.statusEl && this.statusEl.classList.contains('is-error')) this.clearStatus();
        const q = this.input.value.trim();
        if (q.length < MIN_CHARS) { this.closeMenu(); return; }
        this.timer = setTimeout(() => this.search(q), DEBOUNCE_MS);
      });

      this.input.addEventListener('keydown', (e) => {
        const items = this.menu.querySelectorAll('.dni-item');
        if (!items.length) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          this.activeIndex = e.key === 'ArrowDown'
            ? Math.min(this.activeIndex + 1, items.length - 1)
            : Math.max(this.activeIndex - 1, 0);
          items.forEach((el, i) => {
            const active = i === this.activeIndex;
            el.classList.toggle('is-active', active);
            el.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          const activeEl = items[this.activeIndex];
          this.input.setAttribute('aria-activedescendant', activeEl.id);
          activeEl.scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'Enter' && this.activeIndex > -1 && this.activeIndex < items.length) {
          e.preventDefault();
          items[this.activeIndex].dispatchEvent(new MouseEvent('mousedown'));
        } else if (e.key === 'Escape') {
          this.closeMenu();
        }
      });

      this.onDocumentClick = (e) => {
        if (!this.contains(e.target)) this.closeMenu();
      };
      document.addEventListener('click', this.onDocumentClick);
    }

    openMenu() {
      this.menu.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
    }

    closeMenu() {
      // Abort any in-flight search so a late response can't reopen the menu
      // over an input the user has already cleared or shortened.
      if (this.controller) { this.controller.abort(); this.controller = null; }
      this.menu.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
    }

    mapResults(json) {
      const arr = Array.isArray(json)
        ? json
        : ((json && (json.data || json.results || json.items)) || []);
      return arr.map((it) => ({
        id: it.drugId,
        label: it.name || '',
        detail: it.brandName || '',
        raw: it
      }));
    }

    renderMenu(items) {
      // Fresh results invalidate any highlighted row from the previous query.
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.menu.innerHTML = '';
      if (!items.length) {
        this.menu.innerHTML = '<li class="dni-empty">No matches found</li>';
        this.openMenu();
        return;
      }
      items.forEach((it, i) => {
        const li = document.createElement('li');
        li.className = 'dni-item';
        li.id = `dni-opt-${this.sectionId}-${i}`;
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        // Brand name leads in bold, generic name follows in parentheses:
        // "TESTIM (TESTOSTERONE)". Drugs with no distinct brand show the
        // generic name alone.
        const brand = it.detail && it.detail !== it.label ? it.detail : '';
        const strong = document.createElement('strong');
        strong.textContent = brand || it.label;
        li.appendChild(strong);
        if (brand) {
          const span = document.createElement('span');
          span.textContent = `(${it.label})`;
          li.appendChild(span);
        }
        li.addEventListener('mousedown', (e) => { e.preventDefault(); this.select(it); });
        this.menu.appendChild(li);
      });
      this.openMenu();
    }

    async search(q) {
      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.menu.innerHTML = '<li class="dni-loading">Searching…</li>';
      this.openMenu();
      const url = `${this.searchApi}${this.searchApi.includes('?') ? '&' : '?'}query=${encodeURIComponent(q)}`;
      try {
        const r = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!r.ok) throw new Error(r.status);
        const json = await r.json();
        this.renderMenu(this.mapResults(json));
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.menu.innerHTML = '<li class="dni-empty">Something went wrong. Please try again.</li>';
        this.openMenu();
      }
    }

    setStatus(message, isError) {
      if (!this.statusEl) return;
      this.statusEl.textContent = message;
      this.statusEl.hidden = false;
      this.statusEl.classList.toggle('is-error', !!isError);
      this.statusEl.classList.toggle('is-loading', !isError);
    }

    clearStatus() {
      if (!this.statusEl) return;
      this.statusEl.textContent = '';
      this.statusEl.hidden = true;
      this.statusEl.classList.remove('is-error', 'is-loading');
    }

    /* The detail API is a batch endpoint: it takes every selected drug id and
       returns the interactions for that combination, already grouped by
       ingredient. Responses are cached per combination so removing a chip
       (a combination we have already asked for) needs no round trip. */
    async fetchInteractions(ids) {
      const key = ids.slice().sort().join(',');
      if (this.detailCache[key]) return this.detailCache[key];

      if (this.detailController) this.detailController.abort();
      const controller = new AbortController();
      this.detailController = controller;

      const r = await fetch(this.detailApi, {
        method: 'POST',
        signal: controller.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ DrugIds: ids.map(String) })
      });
      if (!r.ok) throw new Error(r.status);
      const json = await r.json();
      if (json && json.success === false) throw new Error(json.errors || 'request failed');
      const data = (json && json.data) || json;
      this.detailCache[key] = data;
      return data;
    }

    async select(item) {
      this.input.value = '';
      this.closeMenu();
      if (this.selected[item.id] || this.pendingDetails[item.id]) return;
      this.pendingDetails[item.id] = true;
      this.setStatus(`Loading details for ${item.label}…`, false);
      try {
        const ids = Object.keys(this.selected).concat(String(item.id));
        const data = await this.fetchInteractions(ids);
        // The batch response covers the whole selection, so the chip label
        // comes from the search result rather than the interaction payload.
        this.selected[item.id] = { name: item.label, brandName: item.detail };
        this.interactions = data;
        this.clearStatus();
        this.renderChips();
        this.renderPanels();
      } catch (e) {
        if (e.name === 'AbortError') return;
        this.setStatus(`Couldn’t load details for ${item.label}. Please try again.`, true);
      } finally {
        delete this.pendingDetails[item.id];
      }
    }

    async removeDrug(id) {
      delete this.selected[id];
      this.renderChips();
      const ids = Object.keys(this.selected);
      if (!ids.length) {
        this.interactions = null;
        this.renderPanels();
        return;
      }
      try {
        this.interactions = await this.fetchInteractions(ids);
        this.clearStatus();
      } catch (e) {
        if (e.name === 'AbortError') return;
        // Keep the previous results rather than blanking the panels.
        this.setStatus('Couldn’t refresh your results. Please try again.', true);
      }
      this.renderPanels();
    }

    renderChips() {
      this.chipsEl.innerHTML = '';
      const ids = Object.keys(this.selected);
      this.resultBlocks.forEach((el) => el.classList.toggle('is-visible', ids.length > 0));
      ids.forEach((id) => {
        const d = this.selected[id];
        const col = document.createElement('div');
        col.className = 'dni-chip-col';
        const chip = document.createElement('div');
        chip.className = 'dni-chip';
        const label = document.createElement('span');
        label.textContent = (d.brandName && d.brandName !== d.name ? `${d.brandName} ` : '') + d.name;
        const x = document.createElement('button');
        x.type = 'button';
        x.setAttribute('aria-label', `Remove ${d.name}`);
        x.textContent = 'X';
        x.addEventListener('click', () => this.removeDrug(id));
        chip.appendChild(label);
        chip.appendChild(x);
        col.appendChild(chip);
        this.chipsEl.appendChild(col);
      });
    }

    /* ---- results tabs (keyboard accessible) ---- */
    initTabs() {
      const tabs = [...this.querySelectorAll('.dni-tab')];
      if (!tabs.length) return;
      this.wireTabs(tabs, [...this.querySelectorAll('.dni-panel')], this.querySelector('.dni-panel-track'));
    }

    /* Wires a tablist to its panels: click + arrow keys, with the sliding
       transition between panels. Used by the result tabs and by the nutrient
       sub-tabs inside each panel. */
    wireTabs(tabs, panels, track) {
      const activate = (tab, focus) => {
        // Finish any slide still running so the panel states stay in sync
        // when tabs are clicked in quick succession.
        if (track.endSlide) track.endSlide();

        const from = panels.find((p) => p.classList.contains('is-active'));
        const to = panels[tabs.indexOf(tab)];

        tabs.forEach((t) => {
          const active = t === tab;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
          t.tabIndex = active ? 0 : -1;
        });

        if (to && from && to !== from && !this.reduceMotion) {
          this.slidePanels(from, to, panels.indexOf(to) > panels.indexOf(from) ? 'next' : 'prev', track);
        } else {
          panels.forEach((p) => p.classList.toggle('is-active', p === to));
        }

        if (focus) tab.focus();
      };

      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => activate(tab, false));
        tab.addEventListener('keydown', (e) => {
          let next;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = tabs[(i - 1 + tabs.length) % tabs.length];
          else if (e.key === 'Home') next = tabs[0];
          else if (e.key === 'End') next = tabs[tabs.length - 1];
          else return;
          e.preventDefault();
          activate(next, true);
        });
      });
    }

    /* Slide the outgoing panel out and the incoming one in, matching the
       Quasar `q-tab-panels animated` transition used on the original page:
       300ms transform only, moving in the direction of travel. */
    slidePanels(from, to, dir, track) {
      const enterFrom = `dni-slide-${dir}-from`;
      const leaveTo = `dni-slide-${dir}-to`;

      track.classList.add('is-animating');
      from.classList.remove('is-active');
      from.classList.add('is-leaving');
      to.classList.add('is-active', enterFrom);

      // Flush the start transforms so the browser animates from them.
      void to.offsetWidth;

      from.classList.add('is-sliding', leaveTo);
      to.classList.add('is-sliding');
      to.classList.remove(enterFrom);

      const onEnd = (e) => {
        if (e.target === to && e.propertyName === 'transform') track.endSlide();
      };
      to.addEventListener('transitionend', onEnd);
      // Fallback for a transitionend that never arrives (interrupted/hidden tab).
      const timer = setTimeout(() => track.endSlide(), 500);

      track.endSlide = () => {
        clearTimeout(timer);
        to.removeEventListener('transitionend', onEnd);
        track.classList.remove('is-animating');
        from.classList.remove('is-leaving', 'is-sliding', leaveTo);
        to.classList.remove('is-sliding', enterFrom);
        track.endSlide = null;
      };
    }

    /* A category entry is a nutrient/ingredient group holding one item per
       interacting drug class:
         excludeIfTaking: [{ ingredientName, items: [{ copyId, copy,
                             skus: [{ sku, value }] }, ...] }, ...]
       A group without `items` is read as a single item, and a bare handle
       string is still accepted; neither can label a nutrient tab, so those
       fall back to a flat product grid. */
    entryItems(entry) {
      if (!entry || typeof entry !== 'object') return entry ? [entry] : [];
      return Array.isArray(entry.items) ? entry.items : [entry];
    }

    /* Product handles for one item, each with the `value` the API sends beside
       it to rank how strongly the product answers the interaction. `sku` is the
       Shopify product handle; a bare handle string carries no value. */
    itemSkus(item) {
      if (typeof item === 'string') return [{ handle: item, value: 0 }];
      if (!item || typeof item !== 'object') return [];
      const list = item.skus || item.products || item.handles || [];
      if (!Array.isArray(list)) return [];
      return list
        .map((p) => {
          if (typeof p === 'string') return { handle: p, value: 0 };
          const handle = p && (p.sku || p.handle || p.productHandle);
          return handle ? { handle, value: Number(p.value) || 0 } : null;
        })
        .filter(Boolean);
    }

    /* Adds one item's handles to `ranked`, an accumulator of { handle, value }
       in arrival order. The same product can come from several drug classes, so
       a repeat keeps the highest value rather than the first one seen. */
    addItemSkus(ranked, item) {
      this.itemSkus(item).forEach(({ handle, value }) => {
        const seen = ranked.find((r) => r.handle === handle);
        if (!seen) ranked.push({ handle, value });
        else if (value > seen.value) seen.value = value;
      });
    }

    /* Handles highest value first, so the carousel's limited slots go to the
       strongest matches. Ties hold the order the API returned them in. */
    rankedHandles(ranked) {
      return ranked
        .map((r, i) => ({ handle: r.handle, value: r.value, i }))
        .sort((a, b) => b.value - a.value || a.i - b.i)
        .map((r) => r.handle);
    }

    /* Entries for one category across the batch response, which may be a
       single merged object or one object per requested drug. */
    categoryEntries(key) {
      if (!this.interactions) return [];
      const sources = Array.isArray(this.interactions) ? this.interactions : [this.interactions];
      const out = [];
      sources.forEach((src) => {
        const list = src && src[key];
        if (Array.isArray(list)) out.push(...list);
      });
      return out;
    }

    collectHandles(key) {
      const ranked = [];
      this.categoryEntries(key).forEach((entry) => {
        this.entryItems(entry).forEach((item) => this.addItemSkus(ranked, item));
      });
      return this.rankedHandles(ranked);
    }

    /* Groups entries by ingredient, merging across the selected drugs. Each
       interacting drug class contributes its own paragraph of copy plus the
       `(reference)` link that follows it on the original, deduped by copyId so
       two drugs sharing an explanation only show it once.
       Returns null when the response carries no ingredient names, so the
       panel can fall back to a flat product grid. */
    collectIngredients(key) {
      const groups = [];
      const byName = {};
      this.categoryEntries(key).forEach((entry) => {
        if (!entry || typeof entry !== 'object') return;
        const name = entry.ingredientName || entry.ingredient || entry.name;
        if (!name) return;
        let group = byName[name];
        if (!group) {
          group = byName[name] = { name, copies: [], seenCopy: {}, ranked: [], handles: [] };
          groups.push(group);
        }
        this.entryItems(entry).forEach((item) => {
          if (!item || typeof item !== 'object') return;
          const copy = item.copy || item.ingredientCopy || item.description || '';
          const copyKey = item.copyId != null ? `id:${item.copyId}` : copy;
          if (copy && !group.seenCopy[copyKey]) {
            group.seenCopy[copyKey] = true;
            group.copies.push({ text: copy.trim(), reference: item.referenceLink || '' });
          }
          this.addItemSkus(group.ranked, item);
        });
      });
      groups.forEach((g) => { g.handles = this.rankedHandles(g.ranked); });
      return groups.length ? groups : null;
    }

    async fetchProduct(handle) {
      if (this.productCache[handle] !== undefined) return this.productCache[handle];
      let product = null;
      try {
        const r = await fetch(`/products/${handle}.js`);
        if (!r.ok) throw new Error(r.status);
        product = await r.json();
      } catch (e) {
        product = null;
      }
      this.productCache[handle] = product;
      return product;
    }

    /* Resolves handles to products until `limit` of them come back, so a handle
       that no longer exists in the shop costs a slot in the request batch
       rather than a slot in the carousel. */
    async fetchProducts(handles, limit) {
      const out = [];
      let i = 0;
      while (i < handles.length && out.length < limit) {
        const batch = handles.slice(i, i + (limit - out.length));
        i += batch.length;
        const products = await Promise.all(batch.map((h) => this.fetchProduct(h)));
        products.forEach((p) => { if (p) out.push(p); });
      }
      return out;
    }

    productCard(product) {
      const a = document.createElement('a');
      a.className = 'dni-product';
      a.href = product.url || `/products/${product.handle}`;
      // Open in a new tab so the entered medications and results survive.
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      const img = document.createElement('img');
      img.loading = 'lazy';
      img.width = 140;
      img.height = 140;
      img.alt = product.title;
      const src = product.featured_image || (product.images && product.images[0]) || '';
      if (src) img.src = src;
      const title = document.createElement('p');
      title.className = 'dni-product-title';
      title.textContent = product.title;
      const price = document.createElement('p');
      price.className = 'dni-product-price';
      price.textContent = product.price != null ? this.formatMoney(product.price) : '';
      a.appendChild(img); a.appendChild(title); a.appendChild(price);
      return a;
    }

    carouselArrow(dir, onClick) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `dni-carousel-arrow is-${dir}`;
      btn.setAttribute('aria-label', dir === 'prev' ? 'Show previous products' : 'Show next products');
      // Material `keyboard_arrow_left` / `keyboard_arrow_right`, as on the original.
      btn.innerHTML = dir === 'prev'
        ? '<svg viewBox="0 0 24 24" width="36" height="36" aria-hidden="true"><path fill="currentColor" d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>'
        : '<svg viewBox="0 0 24 24" width="36" height="36" aria-hidden="true"><path fill="currentColor" d="M8.59 16.59 10 18l6-6-6-6-1.41 1.41L13.17 12z"/></svg>';
      btn.addEventListener('click', onClick);
      return btn;
    }

    /* Product carousel, matching the original's ProductsCarousel: four cards at
       a time out of the first eight products, arrows shifting the window by one
       and wrapping around, and no arrows at all when everything already fits. */
    async renderProducts(container, handles) {
      // Guard against overlapping async renders of the same container (quick
      // add/remove): only the latest call is allowed to populate it.
      const token = (container.dniRenderToken || 0) + 1;
      container.dniRenderToken = token;
      container.innerHTML = '';
      if (!handles.length) return false;

      const products = await this.fetchProducts(handles, MAX_PRODUCTS);
      if (container.dniRenderToken !== token) return true;
      if (!products.length) return false;

      const carousel = document.createElement('div');
      carousel.className = 'dni-carousel';
      const row = document.createElement('div');
      row.className = 'dni-products-row';
      const visible = Math.min(PRODUCTS_PER_PAGE, products.length);

      let start = 0;
      const draw = () => {
        row.innerHTML = '';
        for (let i = 0; i < visible; i++) {
          row.appendChild(this.productCard(products[(start + i) % products.length]));
        }
      };
      const step = (delta) => {
        start = (start + delta + products.length) % products.length;
        draw();
      };

      if (products.length > PRODUCTS_PER_PAGE) {
        carousel.appendChild(this.carouselArrow('prev', () => step(-1)));
        carousel.appendChild(row);
        carousel.appendChild(this.carouselArrow('next', () => step(1)));
      } else {
        carousel.appendChild(row);
      }

      draw();
      container.appendChild(carousel);
      return true;
    }

    renderPanels() {
      this.renderPanel(this.querySelector('.dni-panel[data-panel=support]'), 'includeIfTaking');
      this.renderPanel(this.querySelector('.dni-panel[data-panel=interaction]'), 'excludeIfTaking');
    }

    async renderPanel(panel, key) {
      // Only the most recent render may set the panel's empty state.
      const token = (panel.dniPanelToken || 0) + 1;
      panel.dniPanelToken = token;
      const ingredients = panel.querySelector('[data-ingredients]');
      const grid = panel.querySelector('[data-products]');
      const empty = panel.querySelector('[data-empty]');
      const groups = this.collectIngredients(key);

      if (groups) {
        grid.hidden = true;
        grid.innerHTML = '';
        ingredients.hidden = false;
        this.renderIngredients(panel, ingredients, groups);
        this.setEmpty(panel, empty, false);
        return;
      }

      // Ungrouped handles: no nutrient tabs, just the products.
      ingredients.hidden = true;
      ingredients.querySelector('.dni-ingredient-tabs').innerHTML = '';
      ingredients.querySelector('[data-sub-track]').innerHTML = '';
      grid.innerHTML = '';
      // A panel that never shows products has nothing left to fall back to.
      if (panel.hasAttribute('data-no-products')) {
        grid.hidden = true;
        this.setEmpty(panel, empty, true);
        return;
      }
      grid.hidden = false;
      const hasProducts = await this.renderProducts(grid, this.collectHandles(key));
      if (panel.dniPanelToken !== token) return;
      this.setEmpty(panel, empty, !hasProducts);
    }

    /* The original centres the explanation and the "nothing found" message in
       the full-height panel, so flag the state for CSS. */
    setEmpty(panel, empty, isEmpty) {
      empty.hidden = !isEmpty;
      panel.classList.toggle('is-empty', isEmpty);
    }

    /* The `(reference)` citation the original appends to each paragraph of copy.
       The href comes from the API, which is no more trusted than the copy, so
       anything that isn't a plain http(s) URL is dropped rather than handed to
       the browser as a navigable link. Returns null when there is nothing safe
       to render. */
    referenceLink(href) {
      // An empty value resolves to the storefront root, which is not a citation.
      if (typeof href !== 'string' || !href.trim()) return null;
      let url;
      try {
        url = new URL(href, window.location.origin);
      } catch (e) {
        return null;
      }
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;

      const wrap = document.createElement('span');
      wrap.className = 'dni-reference';
      wrap.appendChild(document.createTextNode(' '));
      const a = document.createElement('a');
      a.href = url.href;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.textContent = '(reference)';
      wrap.appendChild(a);
      return wrap;
    }

    renderIngredients(panel, ingredients, groups) {
      const tabList = ingredients.querySelector('.dni-ingredient-tabs');
      const track = ingredients.querySelector('[data-sub-track]');
      if (track.endSlide) track.endSlide();
      tabList.innerHTML = '';
      track.innerHTML = '';

      const tabs = [];
      const subpanels = [];
      groups.forEach((group, i) => {
        const id = `dni-ing-${this.sectionId}-${panel.dataset.panel}-${i}`;

        const tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'dni-ingredient-tab' + (i === 0 ? ' is-active' : '');
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        tab.setAttribute('aria-controls', id);
        tab.tabIndex = i === 0 ? 0 : -1;
        const label = document.createElement('span');
        label.className = 'dni-ingredient-label';
        label.textContent = group.name;
        tab.appendChild(label);
        tabList.appendChild(tab);
        tabs.push(tab);

        const sub = document.createElement('div');
        sub.className = 'dni-subpanel dni-slide' + (i === 0 ? ' is-active' : '');
        sub.id = id;
        sub.setAttribute('role', 'tabpanel');

        if (group.copies.length) {
          const copyRow = document.createElement('div');
          copyRow.className = 'dni-ingredient-copy';
          const icon = panel.dataset.icon;
          if (icon) {
            const img = document.createElement('img');
            img.className = 'dni-ingredient-icon';
            img.src = icon;
            img.alt = '';
            img.width = 80;
            img.height = 80;
            img.loading = 'lazy';
            copyRow.appendChild(img);
          }
          const copy = document.createElement('div');
          copy.className = 'dni-copy-text';
          group.copies.forEach((entry) => {
            const p = document.createElement('p');
            // Plain text: the copy comes from the API and is never trusted as markup.
            p.textContent = entry.text;
            const ref = entry.reference && this.referenceLink(entry.reference);
            if (ref) p.appendChild(ref);
            copy.appendChild(p);
          });
          copyRow.appendChild(copy);
          sub.appendChild(copyRow);
        }

        // The Possible Interaction side is copy only, as on the original.
        if (!panel.hasAttribute('data-no-products')) {
          // Only the support side gets the shop heading, as on the original.
          if (panel.dataset.shopHeading && group.handles.length) {
            const heading = document.createElement('p');
            heading.className = 'dni-shop-heading';
            heading.textContent = panel.dataset.shopHeading;
            sub.appendChild(heading);
          }

          const grid = document.createElement('div');
          grid.className = 'dni-products';
          sub.appendChild(grid);
          this.renderProducts(grid, group.handles);
        }

        track.appendChild(sub);
        subpanels.push(sub);
      });

      this.wireTabs(tabs, subpanels, track);
    }
  }

  customElements.define('drug-interactions-page', DrugInteractionsPage);
})();